import type React from 'react'
import { useState } from 'react'

export interface PipelineOverrides {
  useVsearchInsteadOfSwarm: boolean
  maxSequencesPerSample?: number
  maxSamples?: number
  consensusCallingMaxReads?: number
}

export interface PipelineOverridesWidgetProps {
  onOverridesChange: (overrides: PipelineOverrides | null) => void
}

export const PipelineOverridesWidget: React.FC<PipelineOverridesWidgetProps> = ({ onOverridesChange }) => {
  const [pipelineOverridesEnabled, setPipelineOverridesEnabled] = useState<boolean>(false)
  const [useVsearchInsteadOfSwarm, setUseVsearchInsteadOfSwarm] = useState<boolean>(false)
  const [maxSequencesPerSample, setMaxSequencesPerSample] = useState<number | undefined>(undefined)
  const [maxSamples, setMaxSamples] = useState<number | undefined>(undefined)
  const [consensusCallingMaxReads, setConsensusCallingMaxReads] = useState<number | undefined>(undefined)

  const handleToggleEnabled = (enabled: boolean) => {
    setPipelineOverridesEnabled(enabled)
    if (!enabled) {
      onOverridesChange(null)
    } else {
      onOverridesChange({
        useVsearchInsteadOfSwarm,
        maxSequencesPerSample,
        maxSamples,
        consensusCallingMaxReads
      })
    }
  }

  const updateOverrides = (partial: Partial<PipelineOverrides>) => {
    const next = {
      useVsearchInsteadOfSwarm,
      maxSequencesPerSample,
      maxSamples,
      consensusCallingMaxReads,
      ...partial
    }
    // `in`, not `!== undefined`: clearing a number field passes undefined and must reset it.
    if (partial.useVsearchInsteadOfSwarm !== undefined) setUseVsearchInsteadOfSwarm(partial.useVsearchInsteadOfSwarm)
    if ('maxSequencesPerSample' in partial) setMaxSequencesPerSample(partial.maxSequencesPerSample)
    if ('maxSamples' in partial) setMaxSamples(partial.maxSamples)
    if ('consensusCallingMaxReads' in partial) setConsensusCallingMaxReads(partial.consensusCallingMaxReads)

    if (pipelineOverridesEnabled) {
      onOverridesChange(next)
    }
  }

  return (
    <div className="mt-4 border-t pt-4">
      <label className="flex items-center space-x-2 cursor-pointer">
        <input
          type="checkbox"
          className="checkbox checkbox-secondary"
          checked={pipelineOverridesEnabled}
          onChange={(e) => handleToggleEnabled(e.target.checked)}
        />
        <span className="font-bold">pipeline overrides</span>
      </label>

      {pipelineOverridesEnabled && (
        <div className="ml-6 mt-2 p-3 border rounded bg-gray-50 space-y-4">
          <div>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                className="checkbox checkbox-sm"
                checked={useVsearchInsteadOfSwarm}
                onChange={(e) => updateOverrides({ useVsearchInsteadOfSwarm: e.target.checked })}
              />
              <span>use vsearch instead of swarm</span>
            </label>
            <p className="text-xs text-gray-500 ml-6">If enabled, the pipeline will use VSEARCH for clustering instead of SWARM.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">max sequences per sample</label>
            <input
              type="number"
              className="input input-bordered input-sm w-full"
              placeholder="e.g. 10000"
              value={maxSequencesPerSample ?? ''}
              onChange={(e) => updateOverrides({ maxSequencesPerSample: e.target.value === '' ? undefined : parseInt(e.target.value, 10) })}
            />
            <p className="text-xs text-gray-500">Limits the number of sequences processed per sample. Leave empty for no limit.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">max samples</label>
            <input
              type="number"
              className="input input-bordered input-sm w-full"
              placeholder="e.g. 50"
              value={maxSamples ?? ''}
              onChange={(e) => updateOverrides({ maxSamples: e.target.value === '' ? undefined : parseInt(e.target.value, 10) })}
            />
            <p className="text-xs text-gray-500">Limits the total number of samples processed in this order. Leave empty for no limit.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">consensus calling max reads</label>
            <input
              type="number"
              className="input input-bordered input-sm w-full"
              placeholder="default 20000"
              data-testid="pipeline-override-consensus-calling-max-reads"
              value={consensusCallingMaxReads ?? ''}
              onChange={(e) =>
                updateOverrides({ consensusCallingMaxReads: e.target.value === '' ? undefined : parseInt(e.target.value, 10) })
              }
            />
            <p className="text-xs text-gray-500">
              Nanopore (ONT) only: the most reads per sample used for consensus calling. Leave empty for the pipeline default of 20,000.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
